import { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';
import { EcommerceElement, EcommerceCreativeName, Context } from '@pegasus/tracker';
import { trackViewPromotion } from '@browsing/tracking';

import { Campaign } from '@shared/types/mylounge';

export interface TrackingParams {
    element: EcommerceElement;
    creativeName: EcommerceCreativeName;
    itemListName: Context;
}

interface UseTrackCampaignInViewParams {
    campaigns: Campaign[];
    categoryTabId: string | undefined;
    trackingParams: TrackingParams;
}

const useTrackCampaignInView = ({
    campaigns,
    categoryTabId,
    trackingParams,
}: UseTrackCampaignInViewParams) => {
    const [visibleCampaigns, trackedCampaigns] = useMemo(() => {
        return [new Set<string>(), new Set<string>()];
    }, []);

    const campaignsRef = useRef(campaigns);
    const categoryTabIdRef = useRef(categoryTabId);
    const trackingParamsRef = useRef(trackingParams);

    campaignsRef.current = campaigns;
    categoryTabIdRef.current = categoryTabId;
    trackingParamsRef.current = trackingParams;

    const debouncedTrack = useMemo(
        () =>
            debounce(() => {
                if (visibleCampaigns.size === 0) {
                    return;
                }

                const newCampaignsToTrack = Array.from(visibleCampaigns).filter(
                    campaignId => !trackedCampaigns.has(campaignId),
                );

                if (newCampaignsToTrack.length === 0) {
                    return;
                }

                newCampaignsToTrack.forEach(campaignId => {
                    trackedCampaigns.add(campaignId);
                });

                const items = newCampaignsToTrack.map(campaignId => {
                    const campaignIndex = campaignsRef.current.findIndex(
                        c => c.campaignId === campaignId,
                    );
                    return {
                        campaignIdentifier: campaignId,
                        categoryId: categoryTabIdRef.current,
                        index: campaignIndex,
                    };
                });

                trackViewPromotion({
                    ...trackingParamsRef.current,
                    items,
                });
            }, 500),
        [],
    );

    useEffect(() => {
        return () => debouncedTrack.cancel();
    }, [debouncedTrack]);

    const handleTrackCampaignInView = useCallback(
        (campaign: Campaign, isVisible: boolean) => {
            if (isVisible) {
                visibleCampaigns.add(campaign.campaignId);
            } else {
                visibleCampaigns.delete(campaign.campaignId);
            }

            debouncedTrack();
        },
        [visibleCampaigns, debouncedTrack],
    );

    return handleTrackCampaignInView;
};

export { useTrackCampaignInView };
